// HUMINT voices: similar voices of two persons, and whose voice is in a recording.
//
// SERVED: the comparison of stored voiceprints — the open leads of the case and their count, one
// pair, its decision, reopening and merge, and a subject's `voice_leads` on the subject page and
// in *Subjekty*. The shapes mirror `routers/v1/schemas/case_humint_voice.py`. The leads and the
// pair are audited reads (one `voice.search` row before the answer), so a caller fetches each
// once per view and never in the background; the count answers two numbers, names nobody and is
// not audited, so a badge may poll it. Every change is audited before it is made
// (`voice.decision`, `identity.merge`); one that could not be recorded answers 503
// `audit_unavailable`.
//
// SERVED TOO, in part: who each voice of a recording is. The recording read names its `voices`
// and its `diarization`, and `humint-recording-voices.ts` sets the assignments; a voice's
// `source`, its voiceprint fields and `people_voice` are not sent, because naming a voice makes
// no voiceprint yet.
//
// TYPES ONLY, NOT ON THE SERVER YET: the rest of "The voices of a recording", their check
// (everything under "Checking the voices"), and a person's `voiceprints` on the subject page.
// Those stay a proposal the screens are designed against.
//
// The voice sample itself is `humint-voice.ts`. Imported by path, not via the barrel.
import type { CaseRole } from './cases'
import { apiClient } from './client'
import { humintPath } from './humint-path'
import type { SubjectSummaryDto } from './humint-subject-page'

// ── A voiceprint ─────────────────────────────────────────────────────────────

/** How a voiceprint came about: from a guided sample, or from one voice of a questioning. */
export type VoiceprintOriginDto = 'sample' | 'questioning'

/**
 * Where a voiceprint stands, as a capture's state does: being computed, kept, not made for too
 * little speech (the voice's `no_face`), or failed.
 */
export type VoiceprintStateDto = 'processing' | 'ready' | 'no_speech' | 'failed'

/** The sound of what a voiceprint was made from, as a mark's `feature_quality`. */
export type VoiceprintQualityDto = 'ok' | 'low'

/**
 * Why the sound reads low: a closed vocabulary about the recording, never about the speaker.
 * Sent as strings; a flag the client does not know is left out.
 */
export type VoiceQualityFlagDto = 'too_short' | 'noisy' | 'clipped' | 'overlapping_speech'

/**
 * One stored voiceprint of a person. No vector and nothing derived from one is ever sent.
 *
 * As a side of a lead or of a pair the server sends only stored voiceprints of samples: `state`
 * is `ready`, `quality` null, `quality_flags` empty, `voice` and `encounter_ref` null.
 */
export interface VoiceprintDto {
  /**
   * A UUID. For a voiceprint of a sample it is the sample's capture id (ADR-0062). The client
   * draws the voiceprint's mark from it and from nothing else.
   */
  voiceprint_id: string
  /** The person who holds it now: an identity merge moves it. */
  subject_ref: string
  origin: VoiceprintOriginDto
  state: VoiceprintStateDto
  /** The capture it was made from: the sample, or the recording. */
  capture_id: string
  /** Which voice of that recording (1..n); null for a sample. */
  voice: number | null
  encounter_ref: string | null
  created_at: string | null
  /** Net speech it was made from; null until measured. */
  speech_ms: number | null
  /** Null until computed, and from a server that does not judge it. */
  quality: VoiceprintQualityDto | null
  quality_flags: string[]
  /** Whether comparisons use it. */
  reference: boolean
  /** Voiceprints of two models are not comparable. */
  model_version: string | null
  /** Pairs with another person's voiceprint that wait for an analyst. */
  pending_candidates: number
  /**
   * `/api/v1/files/…` of the audio this voiceprint was made from and only that, fetched with the
   * operator's token: one `file.read` row per fetch. Set in the pair read alone, for a voiceprint
   * of a sample (the sample's own file); null in a list and when there is nothing to play.
   */
  audio_url: string | null
}

/** A voiceprint as a list of people names it: enough for its mark and where it came from. */
export type VoiceprintBriefDto = Pick<VoiceprintDto, 'voiceprint_id' | 'origin' | 'state'>

/**
 * A person's open voice leads, as `tattoo_leads` counts the tattoo ones. The server always sends
 * the object: zero and nulls without a lead.
 */
export interface SubjectVoiceLeadsDto {
  count: number
  /** The person's own voiceprint in the first lead, by the list's order; null for none. */
  voiceprint_id: string | null
  /** That first lead: the comparison's address. Null for none. */
  pair_id: string | null
}

/** What the subject page read adds. The sample itself stays one of `captures`. */
export interface SubjectPageVoiceFields {
  /** In the order they were made. Not served yet: it comes with the voices of a recording. */
  voiceprints?: VoiceprintDto[]
  /** An older server sends none. */
  voice_leads?: SubjectVoiceLeadsDto | null
}

/** What a row of *Subjekty* adds; an older server sends none. */
export interface SubjectListVoiceFields {
  voice_leads?: number
}

// ── The voices of a recording ────────────────────────────────────────────────

/** Whose a voice is said to be: a subject of the recording, or one of the two who are none. */
export type RecordingVoiceRoleDto = 'subject' | 'questioner' | 'interpreter'

/** Why a voice can be named and still makes no voiceprint. */
export type VoiceprintRefusalDto = 'too_little_speech' | 'voices_not_told_apart'

/** Whether the voices were told apart: `failed` and `off` leave everybody with one voice. */
export type DiarizationStatusDto = 'ok' | 'failed' | 'off'

/** One voice of a recording and who it is. `voice` is the turns' number (1..n; 0 never appears). */
export interface RecordingVoiceDto {
  voice: number
  turn_count: number
  speech_ms: number
  /** The recording's holder or one of its participants; null while unassigned, and for a role. */
  subject: SubjectSummaryDto | null
  /** Null while unassigned. */
  role: RecordingVoiceRoleDto | null
  /**
   * The operator picked it, or accepted a proposal. Null while unassigned. Not sent yet: every
   * assignment is the operator's own.
   */
  source?: 'operator' | 'proposal' | null
  assigned_at: string | null
  /**
   * The voiceprint this assignment made of the voice's turns; null when it made none. Not sent
   * yet, like the two fields under it: naming a voice makes no voiceprint.
   */
  voiceprint?: VoiceprintDto | null
  /** Whether assigning this voice to a person without a voiceprint would make one. */
  can_make_voiceprint?: boolean
  voiceprint_refusal?: VoiceprintRefusalDto | null
}

/** What a person of the recording already holds: the summary of a subject carries no biometrics. */
export interface RecordingPersonVoiceDto {
  subject_ref: string
  voiceprints: VoiceprintBriefDto[]
}

/** What the recording read would add. */
export interface RecordingVoiceFields {
  /**
   * One per voice, by number. Null, or left out by an older server, while the recording has no
   * roster: its transcript is not ready, or the graph holds no questioning for it.
   */
  voices?: RecordingVoiceDto[] | null
  /** Null for a recording without a transcript; a transcript that does not say reads `ok`. */
  diarization?: DiarizationStatusDto | null
  /** One entry for the holder and for each participant. Not sent yet. */
  people_voice?: RecordingPersonVoiceDto[]
}

/** One voice's answer: a subject, a role without an identity, or nobody (both null). */
export interface RecordingVoiceAssignmentDto {
  voice: number
  subject_ref: string | null
  role: RecordingVoiceRoleDto | null
}

/** The assignments as a whole, as a recording's participants are set: who is left out is cleared. */
export interface RecordingVoicesRequest {
  assignments: RecordingVoiceAssignmentDto[]
}

/** The state the request left. */
export interface RecordingVoicesResponse {
  capture_id: string
  voices: RecordingVoiceDto[]
  /** Not sent yet. */
  people_voice?: RecordingPersonVoiceDto[]
}

/** A voice in the unaudited state poll: its number and where its voiceprint stands, no person. */
export interface RecordingVoiceStateDto {
  voice: number
  voiceprint_state: VoiceprintStateDto | null
}

/** What the recording's state poll would add. */
export interface RecordingStateVoiceFields {
  voices?: RecordingVoiceStateDto[]
}

// ── Checking the voices: a proposal, never an assignment ─────────────────────

/** The face check's verdicts without *retake*. */
export type SpeakerCheckVerdictDto = 'possible' | 'no_match' | 'not_checked'

/** Why a voice was not checked. */
export type SpeakerCheckReasonDto =
  | 'no_voiceprints'
  | 'too_little_speech'
  | 'model_mismatch'
  | 'reembedding_pending'
  | 'index_not_ready'
  | 'timeout'
  | 'service_unavailable'
  | 'error'

/**
 * What the server looked at when it judged a comparison: reported, never re-derived. A pair
 * lists only what was computed: `similarity` always (the cosine against the upper threshold the
 * pair was written under, so it is not met on a weak pair), `agreement` when either person holds
 * another voiceprint to compare with (the lowest cosine among them, against the lower threshold),
 * `margin` when the case had a runner-up (the lead over it). It never sends `quality`: nothing
 * stored judges the sound.
 */
export interface VoiceConditionDto {
  id: 'similarity' | 'quality' | 'agreement' | 'margin'
  met: boolean
  value: number | null
}

/** A person of the recording whose stored voiceprint is similar to a voice. */
export interface SpeakerCandidateDto {
  subject: SubjectSummaryDto
  voiceprint_id: string
  /** Raw cosine. */
  similarity: number
  /** Only ever `possible`. */
  band: 'possible'
  conditions: VoiceConditionDto[]
}

export interface SpeakerProposalDto {
  voice: number
  speech_ms: number
  verdict: SpeakerCheckVerdictDto
  reason: SpeakerCheckReasonDto | null
  low_quality: boolean
  /** At most three, the most similar first, only among the recording's own people. */
  candidates: SpeakerCandidateDto[]
}

/** The check of one recording's voices against the voiceprints of its people. */
export interface SpeakerCheckResponse {
  /** The audit row's correlation id. */
  check_id: string
  capture_id: string
  checked_at: string
  /** How many stored voiceprints the voices were compared with. */
  scope: { voiceprint_count: number }
  /** One per voice. */
  proposals: SpeakerProposalDto[]
}

// ── Open voice leads (Entity Resolution) ─────────────────────────────────────

/** Where an open lead stands: waiting for same / different, or confirmed and waiting for a merge. */
export type VoiceLeadStateDto = 'to_review' | 'to_merge'

/** One side of a lead: the voiceprint, its owner and a small picture of where it speaks. */
export interface VoiceLeadSideDto {
  voiceprint: VoiceprintDto
  owner: SubjectSummaryDto
  /**
   * About 64 peaks in 0..1 that ride in the read, drawn as they are and never fetched; null when
   * there are none. They show where speech is, and say nothing of how alike two voices are.
   */
  waveform: number[] | null
}

/** A pair of voiceprints of two different persons of the case that waits for an analyst. */
export interface VoiceLeadRowDto {
  pair_id: string
  state: VoiceLeadStateDto
  /**
   * The band the pair was written under: `weak` between the server's two thresholds, `possible`
   * from the upper one. A weak lead is open like any other.
   */
  band: string
  /** Raw cosine. */
  similarity: number
  /**
   * The owners were met in one encounter or appear in one photo: never offered a merge. Only a
   * lead to review can say true (a confirmed pair of such two is no lead).
   */
  seen_together: boolean
  created_at: string | null
  decided_at: string | null
  /** Exactly two, the lower voiceprint id first. */
  sides: VoiceLeadSideDto[]
}

export interface VoiceLeadsResponse {
  /** The audit row's correlation id. */
  search_id: string
  /** At most 200: the ones to merge first, then the most similar, then the newest. */
  items: VoiceLeadRowDto[]
  /** Every open lead of the case. */
  total: number
  member_role: CaseRole
}

/** The open leads of the case as two numbers: no person, voiceprint or pair is named. */
export interface VoiceLeadCountDto {
  to_review: number
  to_merge: number
  /** Suggested recording detections visible to this operator; omitted by older servers. */
  recording_to_review?: number
}

// ── One pair, where a lead is judged ─────────────────────────────────────────

export type VoicePairStatusDto = 'suggested' | 'confirmed' | 'rejected'

/** One side of the comparison. */
export interface VoicePairSideDto extends VoiceLeadSideDto {
  /**
   * `/api/v1/files/…` of the audio this voiceprint was made from and only that: a sample's own
   * file, never a whole recording. One `file.read` row per fetch, so it is asked for on a play
   * and never on opening. Null when nothing can be played.
   */
  audio_url: string | null
  /** That audio's length, for the transport before it is loaded; null when not known. */
  audio_ms: number | null
}

/** The pair as the comparison reads it, whatever its status: an audited read. */
export interface VoicePairDto {
  /** The audit row's correlation id. */
  search_id: string
  pair_id: string
  status: VoicePairStatusDto
  /** The band the pair was written under: `weak` or `possible`. */
  band: string
  /** The thresholds behind the band were not evaluated yet. */
  provisional: boolean
  similarity: number
  conditions: VoiceConditionDto[]
  /** The two persons are linked to one encounter: two people, never offered a merge. */
  seen_together: boolean
  created_at: string | null
  decided_at: string | null
  /** Exactly two, the lower voiceprint id first. */
  sides: VoicePairSideDto[]
  member_role: CaseRole
}

export interface VoicePairDecisionRequest {
  decision: 'same' | 'different'
  note?: string | null
}

/**
 * The pair after a change — or as it stands, for a change that changed nothing — and the counts
 * the change moved.
 */
export interface VoicePairStateDto {
  pair_id: string
  status: VoicePairStatusDto
  /** The pair's two voiceprints, each with its leads still to review. */
  voiceprints: { voiceprint_id: string; pending_candidates: number }[]
  /** Voiceprints of the case with a lead to review. */
  pending_total: number
}

/** The merge of the pair's two persons: the voiceprint whose subject stays, whichever side it is. */
export interface VoicePairMergeRequest {
  survivor_voiceprint_id: string
}

/** The merge made, undone from the entity-resolution audit: the subject that stays. */
export interface VoicePairMergedDto {
  survivor_ref: string
}

const voicePath = (caseId: string, ...segments: string[]) =>
  humintPath(caseId, 'voice', ...segments)

export const humintVoiceMatchingApi = {
  /** The open leads of the case, each with both voiceprints and their owners. Records one `voice.search`. */
  leads: (caseId: string, signal?: AbortSignal) =>
    apiClient.get<VoiceLeadsResponse>(voicePath(caseId, 'leads'), { signal }),

  /** How many leads are open. Not audited: it names nothing, so a badge may poll it. */
  leadsCount: (caseId: string, signal?: AbortSignal) =>
    apiClient.get<VoiceLeadCountDto>(voicePath(caseId, 'leads', 'count'), { signal }),

  /** One pair with what each side plays. Records one `voice.search`. */
  pair: (caseId: string, pairId: string, signal?: AbortSignal) =>
    apiClient.get<VoicePairDto>(voicePath(caseId, 'pairs', pairId), { signal }),

  decide: (caseId: string, pairId: string, body: VoicePairDecisionRequest) =>
    apiClient.post<VoicePairStateDto>(voicePath(caseId, 'pairs', pairId, 'decision'), body),

  reopen: (caseId: string, pairId: string) =>
    apiClient.post<VoicePairStateDto>(voicePath(caseId, 'pairs', pairId, 'reopen')),

  /** Fold the other voiceprint's subject into the subject of `survivorVoiceprintId`; undone from entity resolution. */
  merge: (caseId: string, pairId: string, survivorVoiceprintId: string) =>
    apiClient.post<VoicePairMergedDto>(voicePath(caseId, 'pairs', pairId, 'merge'), {
      survivor_voiceprint_id: survivorVoiceprintId,
    } satisfies VoicePairMergeRequest),
}
